// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.
import './Marketing.css';
import Footer from '../components/Footer';

const Features = () => {
  const trackCta = (label) => {
    const payload = { event: 'feature_cta', label };
    if (window?.gtag) {
      window.gtag('event', 'click', payload);
    } else if (window?.dataLayer) {
      window.dataLayer.push(payload);
    }
  };

  return (
    <>
      <div className="features-page-v2">
        <div className="features-hero">
          <span className="pill glow">All features</span>
          <h1>Everything Servantica offers</h1>
          <p className="features-lead">
            Voice assistants, communications, AI media, notifications, and developer tools — all in one unified platform.
          </p>
        </div>

        {/* SERVA Voice Assistant */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge serva-bg">🗣️</div>
            <div>
              <h2>SERVA — AI Voice Assistant</h2>
              <p className="muted">Available at <a href="https://serva.servantica.in" target="_blank" rel="noopener noreferrer" className="feat-link serva-link">serva.servantica.in</a></p>
            </div>
          </div>
          <div className="feat-card-grid">
            <div className="feat-card">
              <strong>Wake word activation</strong>
              <p>Say the wake word and SERVA starts listening — no buttons, no tapping.</p>
            </div>
            <div className="feat-card">
              <strong>Offline capable</strong>
              <p>Core voice tasks work without internet using on-device processing.</p>
            </div>
            <div className="feat-card">
              <strong>AI-powered responses</strong>
              <p>Natural language understanding with context-aware answers and task routing.</p>
            </div>
            <div className="feat-card">
              <strong>Workspace integration</strong>
              <p>Connected to SMS, calls, notifications, and the full Servantica dashboard.</p>
            </div>
            <div className="feat-card">
              <strong>Multi-platform</strong>
              <p>Web (live), Android ARVA app (coming soon), and desktop (planned).</p>
            </div>
            <div className="feat-card">
              <strong>Speech-to-text</strong>
              <p>Real-time voice transcription for hands-free note-taking and commands.</p>
            </div>
          </div>
          <a className="feat-launch-btn serva-bg" href="https://serva.servantica.in" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('feat_serva')}>
            Launch SERVA →
          </a>
        </section>

        {/* Notification Service */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge notif-bg">🔔</div>
            <div>
              <h2>Notification Service</h2>
              <p className="muted">Live at <a href="https://notification.servantica.in/" target="_blank" rel="noopener noreferrer" className="feat-link notif-link-feat">notification.servantica.in</a></p>
            </div>
          </div>
          <div className="feat-card-grid">
            <div className="feat-card">
              <strong>Email delivery</strong>
              <p>SMTP-powered via Hostinger with custom templates, HTML support, and delivery tracking.</p>
            </div>
            <div className="feat-card">
              <strong>SMS messaging</strong>
              <p>Twilio-backed SMS with bulk send, scheduling, and per-message status tracking in MongoDB.</p>
            </div>
            <div className="feat-card">
              <strong>Push notifications</strong>
              <p>Real-time admin push alerts, unread badges, and non-intrusive toast notifications.</p>
            </div>
            <div className="feat-card">
              <strong>Birthday reminders</strong>
              <p>Automated daily scheduler at 10 AM sends personalized birthday wishes to contacts.</p>
            </div>
            <div className="feat-card">
              <strong>REST API</strong>
              <p>Full Swagger-documented API with JWT auth, Auth0, and Keycloak support.</p>
            </div>
            <div className="feat-card">
              <strong>Health monitoring</strong>
              <p>Prometheus metrics, actuator endpoints, and real-time observability dashboard.</p>
            </div>
          </div>
          <a className="feat-launch-btn notif-bg" href="https://notification.servantica.in/" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('feat_notification')}>
            Open Notification Service →
          </a>
        </section>

        {/* SMS + Calls */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge comms-bg">📡</div>
            <div>
              <h2>SMS + Calls</h2>
              <p className="muted">Integrated communication tools in the Servantica dashboard</p>
            </div>
          </div>
          <div className="feat-card-grid">
            <div className="feat-card">
              <strong>Send SMS</strong>
              <p>Compose and send individual or bulk SMS messages with delivery receipts.</p>
            </div>
            <div className="feat-card">
              <strong>Schedule messages</strong>
              <p>Queue messages for future delivery with timezone-aware scheduling.</p>
            </div>
            <div className="feat-card">
              <strong>Outbound calls</strong>
              <p>Place calls with attached notes and real-time call status tracking.</p>
            </div>
            <div className="feat-card">
              <strong>Call reports</strong>
              <p>View call history, duration, and status reports from the dashboard.</p>
            </div>
            <div className="feat-card">
              <strong>Twilio powered</strong>
              <p>Enterprise-grade telephony infrastructure for reliable SMS and voice delivery.</p>
            </div>
            <div className="feat-card">
              <strong>Per-user access</strong>
              <p>Email-aware gating with Razorpay unlock per feature, per user.</p>
            </div>
          </div>
          <a className="feat-launch-btn comms-bg" href="/login?returnTo=/dashboard/sms?mode=send" onClick={() => trackCta('feat_sms')}>
            Open SMS Dashboard →
          </a>
        </section>

        {/* AI Studio */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge ai-bg">🎨</div>
            <div>
              <h2>AI Media Studio</h2>
              <p className="muted">Create images and videos from text prompts</p>
            </div>
          </div>
          <div className="feat-card-grid">
            <div className="feat-card">
              <strong>Image generation</strong>
              <p>Turn text prompts into unique, high-quality images in seconds.</p>
              <span className="pill soft" style={{ marginTop: 8 }}>Available</span>
            </div>
            <div className="feat-card">
              <strong>Video generation</strong>
              <p>Create engaging video content from simple text descriptions.</p>
              <span className="pill soft soon" style={{ marginTop: 8 }}>Coming soon</span>
            </div>
            <div className="feat-card">
              <strong>Prompt library</strong>
              <p>Example prompts to help you get started with creative AI generation.</p>
            </div>
            <div className="feat-card">
              <strong>Instant download</strong>
              <p>Download generated media immediately — no watermarks, no waiting.</p>
            </div>
          </div>
          <a className="feat-launch-btn ai-bg" href="/login?returnTo=/image-generation" onClick={() => trackCta('feat_image')}>
            Open AI Studio →
          </a>
        </section>

        {/* Speech + Vision */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge speech-bg">🖼️</div>
            <div>
              <h2>Speech + Vision</h2>
              <p className="muted">Voice and visual AI tools built into the workspace</p>
            </div>
          </div>
          <div className="feat-card-grid">
            <div className="feat-card">
              <strong>Speech-to-text</strong>
              <p>Transcribe audio into text in real-time for notes, search, and commands.</p>
            </div>
            <div className="feat-card">
              <strong>Text-to-speech</strong>
              <p>Convert text into natural-sounding voice with multiple language support.</p>
            </div>
            <div className="feat-card">
              <strong>Face detection</strong>
              <p>Detect faces in uploaded images with bounding box visualization.</p>
            </div>
            <div className="feat-card">
              <strong>Image analysis</strong>
              <p>Extract labels, text, and metadata from images using AI vision models.</p>
            </div>
          </div>
          <a className="feat-launch-btn speech-bg" href="/login?returnTo=/dashboard/speech-to-text" onClick={() => trackCta('feat_speech')}>
            Try Speech Tools →
          </a>
        </section>

        {/* Files + Storage */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge files-bg">📂</div>
            <div>
              <h2>File Storage</h2>
              <p className="muted">Upload, manage, and share files from the dashboard</p>
            </div>
          </div>
          <div className="feat-card-grid cols-3">
            <div className="feat-card">
              <strong>Upload + preview</strong>
              <p>Drag-and-drop upload with instant preview for images, PDFs, and documents.</p>
            </div>
            <div className="feat-card">
              <strong>Download + delete</strong>
              <p>Manage files with one-click download and delete from the file manager.</p>
            </div>
            <div className="feat-card">
              <strong>Status cards</strong>
              <p>Clear file status indicators showing upload progress and storage usage.</p>
            </div>
          </div>
        </section>

        {/* Security + Billing */}
        <section className="feat-section">
          <div className="feat-section-header">
            <div className="feat-section-badge security-bg">🔐</div>
            <div>
              <h2>Security + Billing</h2>
              <p className="muted">Enterprise-grade auth and payment integration</p>
            </div>
          </div>
          <div className="feat-card-grid">
            <div className="feat-card">
              <strong>JWT authentication</strong>
              <p>HS256 signed tokens with shared secret across all Servantica services.</p>
            </div>
            <div className="feat-card">
              <strong>Auth0 + Keycloak</strong>
              <p>Support for Auth0 and Keycloak SSO with automatic token exchange.</p>
            </div>
            <div className="feat-card">
              <strong>Razorpay billing</strong>
              <p>One-time ₹5 unlock per feature with email-aware access persistence.</p>
            </div>
            <div className="feat-card">
              <strong>TLS encryption</strong>
              <p>All API communication encrypted in transit with TLS certificates.</p>
            </div>
          </div>
        </section>

        {/* Platform links */}
        <section className="feat-platforms-section">
          <h2>Servantica platform</h2>
          <div className="feat-platform-grid">
            <a className="feat-platform-link" href="https://servantica.in" onClick={() => trackCta('feat_plat_main')}>
              <strong>servantica.in</strong>
              <p>Main workspace + dashboard</p>
            </a>
            <a className="feat-platform-link serva-accent" href="https://serva.servantica.in" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('feat_plat_serva')}>
              <strong>serva.servantica.in</strong>
              <p>SERVA voice assistant</p>
            </a>
            <a className="feat-platform-link notif-accent" href="https://notification.servantica.in/" target="_blank" rel="noopener noreferrer" onClick={() => trackCta('feat_plat_notif')}>
              <strong>notification.servantica.in</strong>
              <p>Notification service API</p>
            </a>
          </div>
        </section>

        <div className="feat-final-cta">
          <a className="btn-primary" href="/login?returnTo=/dashboard" onClick={() => trackCta('feat_get_started')}>
            Get started for ₹5
          </a>
          <a className="btn-ghost" href="/pricing" onClick={() => trackCta('feat_pricing')}>
            View pricing
          </a>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Features;
